
<feedback-display-header [title]="'feedback-session-feedback-display_transcript-header' | translate">
  @if (hasAnalytics$ | async) {
    <div header-filters>
      <mat-form-field goSelect>
        <mat-select
          [disabled]="shouldTease"
          [(ngModel)]="selectedTranscription"
          (selectionChange)="selectOption($event)"
          disableOptionCentering
          panelClass="offsetPanel">
          <mat-select-trigger>
            {{ getSelectedTranscription() }}
          </mat-select-trigger>
          @for (option of transcriptSelectOptions; track option) {
            <mat-option [value]="option">
              {{ transcriptSelectTranslations[option] | translate }}
            </mat-option>
          }
        </mat-select>
        <mat-icon matIconSuffix class="ficon-app-chevron-down"></mat-icon>
      </mat-form-field>
      @if (shouldShowTranscriptAdvancedBadge()) {
        <badge
          [badgeColor]="BadgeColors.SUCCESS"
          [badgeLabel]="'common_advanced-plan' | translate">
        </badge>
      }
      @if (showBetaLabel()) {
        <span class="beta-label" translate="common_beta"></span>
      }
    </div>
  }
  <div header-menu>
    @if (parsedMediaTranscript$ | async; as transcript) {
      @if (!shouldTease && isTranscriptCompleteWithContent(transcript)) {
        <button
          type="button"
          id="transcript-action-menu-btn"
          class="icon-btn"
          [matMenuTriggerFor]="actionsMenu"
          class="btn btn-xs menu-btn"
          [attr.aria-label]="'transcript-viewer-menu_aria-label' | translate">
          <i class="ficon-app-dots-vertical"></i>
        </button>
        <mat-menu #actionsMenu class="action-menu">
          <button
            mat-menu-item
            (click)="downloadTranscript()"
            id="download-transcript-menu-btn">
            <span translate="transcript-viewer-menu_download-transcript"></span>
          </button>
          @if (canEditSpeakers()) {
            <button
              mat-menu-item
              (click)="editSpeakers($event, ButtonSource.Menu)"
              id="edit-speakers-menu-btn">
              <span translate="transcript-viewer-menu_edit-speakers"></span>
            </button>
          }
        </mat-menu>
      }
    }
  </div>
</feedback-display-header>

<!-- Fetching Transcript -->
@if (isLoading$ | async) {
  <div class="loading-view">
    <i class="ficon-spinner ficon-spin"></i>
    <span translate="common_loading"></span>
  </div>
} @else {
  @if ((requestErrorOccurred$ | async) && !shouldTease) {
    @if (!media.isYoutube()) {
      @if (shouldShowLaunchAIZeroState()) {
        <ngx-zero-state class="launch-ai-zero-state">
          <div zero-state-title translate="transcript-viewer-zero-state_launch-ai"></div>
          <div zero-state-subtext>
            <div translate="transcript-viewer-zero-state_launch-ai-subtext"></div>
            <div>
              <button (click)="runAI()" class="run-ai-button primary-btn" [disabled]="isRunningAI$ | async">
                {{'transcript-viewer-zero-state_launch-ai-run-ai' | translate}}
              </button>
            </div>
          </div>
          <div zero-state-graphic-bottom>
            <img class="zero-state-image"
              src="https://staticassets.goreact.com/Rocket.svg"
              alt="{{'transcript-viewer-zero-state_launch-ai' | translate}}">
          </div>
        </ngx-zero-state>
      }
      @if (!shouldShowLaunchAIZeroState()) {
        <ngx-zero-state class="loading-failed-zero-state">
          <div zero-state-title translate="transcript-viewer-zero-state_loading-failed"></div>
          <div zero-state-subtext translate="transcript-viewer-zero-state_loading-failed-subtext"></div>
          <div zero-state-graphic-bottom>
            <img class="loading-failed-zero-state-image zero-state-image"
              src="https://staticassets.goreact.com/floating-astronaut.svg"
              alt="{{'transcript-viewer-zero-state_loading-failed-image-alt' | translate}}">
          </div>
        </ngx-zero-state>
      }
    }
    @if (media.isYoutube()) {
      <ng-container [ngTemplateOutlet]="youtubeZeroState"></ng-container>
    }
  }
}

<!-- Error Fetching Transcript -->

<!-- Transcript Successfully Fetched -->
@if (parsedMediaTranscript$ | async; as transcript) {
	@if (!media.isYoutube()) {
		<section class="transcript-container">
			<!-- Transcript Processing -->
			@if (transcript.status === TranscriptStatus.PROCESSING || transcript.status === TranscriptStatus.QUEUED) {
				<div class="loading-view"
					>
					<i class="ficon-spinner ficon-spin"></i>
					<span translate="transcript-viewer-zero-state_transcript-processing-subtext"></span>
				</div>
			}
			<!-- Transcript Failed -->
			@if (transcript.status === TranscriptStatus.ERROR) {
				<ngx-zero-state>
					<div zero-state-title translate="transcript-viewer-zero-state_loading-failed"></div>
					<div zero-state-subtext translate="transcript-viewer-zero-state_transcript-failed-subtext"></div>
					<div zero-state-graphic-bottom>
						<img class="zero-state-image"
							src="https://staticassets.goreact.com/floating-astronaut.svg"
							alt="{{'transcript-viewer-zero-state_loading-failed-image-alt' | translate}}">
					</div>
				</ngx-zero-state>
			}
			@if (transcript.status === TranscriptStatus.COMPLETED && transcript.sentencesAndChapters.length === 0) {
				<ngx-zero-state class="loading-failed-zero-state">
					<div zero-state-title translate="transcript-viewer-zero-state_audio-lost"></div>
					<div zero-state-subtext translate="transcript-viewer-zero-state_audio-lost-subtext"></div>
					<div zero-state-graphic-bottom>
						<img class="loading-failed-zero-state-image zero-state-image"
							src="https://staticassets.goreact.com/floating-astronaut.svg"
							alt="{{'transcript-viewer-zero-state_loading-failed-image-alt' | translate}}">
					</div>
				</ngx-zero-state>
			}
			<!-- Transcript Complete -->
			@if (isTranscriptCompleteWithContent(transcript)) {
				<section class="tease-container" *teaseWall="teaseWallConfig;">
					<transcript-search
						[searchResults]="searchResults$ | async"
						[currentSearchIndex]="currentSearchIndex"
						(currentSearchIndexChange)="onSearchIndexChange($event)"
						(searchTextChange)="onSearchTextChange($event)"
						>
					</transcript-search>
					@if (stickyChapter$ | async; as stickyChapter) {
						<div
							class="sticky-chapter-title"
							[style.boxShadow]="(scrollStrategy.scrolledIndexChange | async) > 0 ? '0 3px 2px -2px rgba(0, 0, 0, 0.1)' : 'none'"
							>
							<div class="chapter-title" role="heading" aria-level="6" [innerHTML]="stickyChapter.title"></div>
						</div>
					}
					<cdk-virtual-scroll-viewport class="virtual-viewport" [style.overflow-y]="shouldTease ? 'hidden':'auto'">
						<ul role="list" class="sentence-list">
							<li
								role="listitem"
								*cdkVirtualFor="let sentenceOrChapter of transcript.sentencesAndChapters; templateCacheSize: 25;"
								[attr.data-sentence-id]="isChapter(sentenceOrChapter) ? 'chapter-' + sentenceOrChapter.id : 'sentence-' + sentenceOrChapter.id"
								class="transcript-item"
								[ngClass]="sentenceStyles.get(sentenceOrChapter.id)">
								@if (isChapter(sentenceOrChapter)) {
									@if (sentenceOrChapter.id !== (stickyChapter$ | async)?.id) {
										<div class="chapter-title" role="heading" aria-level="6">{{ sentenceOrChapter.title }}</div>
									} @else {
										<div
											class="chapter-title-placeholder"
											[style.display]="sentenceOrChapter.id === transcript.sentencesAndChapters[0]?.id ? 'none' : null"
										></div>
									}
								}
								@if (!isChapter(sentenceOrChapter)) {
									<button [tabindex]="shouldTease ? -1: 0" class="sentence-button" data-cy="transcription-sentence" (click)="onSentenceClick(sentenceOrChapter)"
										[attr.aria-label]="('transcript-viewer_jump-to' | translate) + (sentenceOrChapter.start | formatTime)
									+ ', ' + ('transcript-viewer_speaker' | translate : { speaker: sentenceOrChapter.speaker})
									+ ', ' + sentenceOrChapter.parsedText">
										<span class="time-container">
											<span class="time-text">
												{{ sentenceOrChapter.start | formatTime }}
											</span>
										</span>
										<span class="sentence-details">
											<span class="sentence-speaker" [hidden]="(isMultiSpeaker$ | async) === false">
												@if (sentenceOrChapter.speaker.length === 1) {
													{{ 'transcript-viewer_speaker' | translate: { speaker: sentenceOrChapter.speaker } }}
												}
												@if (sentenceOrChapter.speaker.length > 1) {
													{{ sentenceOrChapter.speaker }}
												}
												@if (canEditSpeakers$ | async) {
													<button
														type="button"
														class="edit-speakers-button icon-btn"
														(click)="editSpeakers($event)"
														[attr.aria-label]="'edit-transcription-speakers_title' | translate">
														<i class="ficon-app-edit"></i>
													</button>
												}
											</span>
											<span class="sentence-text">
												@for (word of sentenceOrChapter.separateWords; track word; let i = $index; let last = $last) {
													<span [ngClass]="getWordStyle(sentenceOrChapter.id, i)">
														{{ word }}
														@if (!last) {
															&#160;
														}
													</span>
												}
											</span>
										</span>
									</button>
									<div class="btn-bar transcript-item-toolbar visible-when-active pull-right" [style.margin-top]="sentenceOrChapter.id === firstSentenceId ? '2px' : null">
										<button
											class="btn btn-xs insert-quote-btn"
											[attr.aria-label]="'transcript-viewer-list-item_insert-quote' | translate"
											[matTooltip]="'transcript-viewer-list-item_insert-quote' | translate"
											matTooltipPosition="below"
											(click)="insertQuote(sentenceOrChapter)">
											<i class="ficon-quote"></i>
										</button>
									</div>
								}
							</li>
						</ul>
					</cdk-virtual-scroll-viewport>
					<jump-to-current-time-button
						[scrollDirection]="scrollDirection$ | async"
						[hasTags]="tagsList && tagsList.length > 0"
						(jumpToCurrentTime)="jumpToCurrentTime()">
					</jump-to-current-time-button>
				</section>
			}
		</section>
	} @else {
		<ng-container [ngTemplateOutlet]="youtubeZeroState"></ng-container>
	}
}

<ng-template #youtubeZeroState>
  <ngx-zero-state class="loading-failed-zero-state">
    <div zero-state-title translate="transcript-viewer-zero-state_youtube"></div>
    <div zero-state-subtext translate="transcript-viewer-zero-state_youtube-subtext"></div>
    <div zero-state-graphic-bottom>
      <img class="loading-failed-zero-state-image zero-state-image"
        src="https://staticassets.goreact.com/floating-astronaut.svg"
        alt="{{'transcript-viewer-zero-state_loading-failed-image-alt' | translate}}">
    </div>
  </ngx-zero-state>
</ng-template>
